import { useState, useCallback } from "react";
import { ReviewRequestEntity } from "../../domain/entities/ReviewEntity";
import { ReviewRepositoryImpl } from "../../data/repositories/ReviewRepositoryImpl";
import {
  GetPendingReviewsUseCase,
  GetMyReviewRequestsUseCase,
  CheckApprovedReviewUseCase,
  CreateReviewRequestUseCase,
  ApproveReviewUseCase,
  RejectReviewUseCase,
} from "../../domain/use-cases/reviews";

// Create repository instance (singleton)
const reviewRepository = new ReviewRepositoryImpl();

// Create use cases
const getPendingReviewsUseCase = new GetPendingReviewsUseCase(reviewRepository);
const getMyReviewRequestsUseCase = new GetMyReviewRequestsUseCase(
  reviewRepository
);
const checkApprovedReviewUseCase = new CheckApprovedReviewUseCase(
  reviewRepository
);
const createReviewRequestUseCase = new CreateReviewRequestUseCase(
  reviewRepository
);
const approveReviewUseCase = new ApproveReviewUseCase(reviewRepository);
const rejectReviewUseCase = new RejectReviewUseCase(reviewRepository);

export interface UseReviewsReturn {
  pendingReviews: ReviewRequestEntity[];
  myRequests: ReviewRequestEntity[];
  loading: boolean;
  error: Error | null;
  fetchPendingReviews: () => Promise<void>;
  fetchMyRequests: () => Promise<void>;
  checkApprovedReview: (params: {
    user_id: number;
    entity_type: string;
    entity_id: number;
    action_code: string;
  }) => Promise<ReviewRequestEntity | null>;
  createReviewRequest: (data: {
    module_code: string;
    action_code: string;
    entity_type: string;
    entity_id: number;
    request_comments?: string;
  }) => Promise<{ review_id: number }>;
  approveReview: (id: number, comments?: string) => Promise<void>;
  rejectReview: (id: number, comments: string) => Promise<void>;
}

export const useReviews = (): UseReviewsReturn => {
  const [pendingReviews, setPendingReviews] = useState<ReviewRequestEntity[]>(
    []
  );
  const [myRequests, setMyRequests] = useState<ReviewRequestEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPendingReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const reviews = await getPendingReviewsUseCase.execute();
      setPendingReviews(reviews);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch pending reviews")
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const requests = await getMyReviewRequestsUseCase.execute();
      setMyRequests(requests);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch my review requests")
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkApprovedReview = useCallback(
    async (params: {
      user_id: number;
      entity_type: string;
      entity_id: number;
      action_code: string;
    }): Promise<ReviewRequestEntity | null> => {
      try {
        setError(null);
        return await checkApprovedReviewUseCase.execute(params);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to check approved review")
        );
        throw err;
      }
    },
    []
  );

  const createReviewRequest = useCallback(
    async (data: {
      module_code: string;
      action_code: string;
      entity_type: string;
      entity_id: number;
      request_comments?: string;
    }): Promise<{ review_id: number }> => {
      try {
        setError(null);
        const result = await createReviewRequestUseCase.execute(data);
        // Refresh my requests
        await fetchMyRequests();
        return result;
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to create review request")
        );
        throw err;
      }
    },
    [fetchMyRequests]
  );

  const approveReview = useCallback(
    async (id: number, comments?: string): Promise<void> => {
      try {
        setError(null);
        await approveReviewUseCase.execute(id, comments);
        // Refresh pending reviews and my requests
        await Promise.all([fetchPendingReviews(), fetchMyRequests()]);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to approve review")
        );
        throw err;
      }
    },
    [fetchPendingReviews, fetchMyRequests]
  );

  const rejectReview = useCallback(
    async (id: number, comments: string): Promise<void> => {
      try {
        setError(null);
        await rejectReviewUseCase.execute(id, comments);
        // Refresh pending reviews and my requests
        await Promise.all([fetchPendingReviews(), fetchMyRequests()]);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to reject review")
        );
        throw err;
      }
    },
    [fetchPendingReviews, fetchMyRequests]
  );

  return {
    pendingReviews,
    myRequests,
    loading,
    error,
    fetchPendingReviews,
    fetchMyRequests,
    checkApprovedReview,
    createReviewRequest,
    approveReview,
    rejectReview,
  };
};
